import {
  Alert,
  Box,
  Button,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { FileGroupView, type FileGroup } from "./components/FileGroupView";
import { FileErrorsView } from "./components/FileErrorsView";

// Type definitions (moved from useLintTriageStore.ts)
export interface ESLintError {
  id: string;
  filePath: string;
  relativeFilePath: string;
  line: number;
  column: number;
  message: string;
  severity: number | string; // Allow both for compatibility

  // ESLint-specific (make optional)
  ruleId?: string;
  variableName?: string;

  // TypeScript-specific (optional)
  errorCode?: string;

  // Jest-specific (optional)
  testName?: string;
  ancestorTitles?: string[];
  fullName?: string;

  source?: "eslint" | "typescript" | "jest";

  // Common
  category: string;
  riskLevel: string;
  codeSnippet: string;
  fixOptions: Array<{
    strategy: string;
    label: string;
    description: string;
    preview?: string;
    recommended?: boolean;
    testUrls?: string[];
  }>;
}

export const LintTriage: React.FC = () => {
  const [errors, setErrors] = useState<ESLintError[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  const loadErrors = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch("http://localhost:9999/api/errors");
      if (!res.ok) {
        throw new Error(`Failed to load errors: ${res.statusText}`);
      }
      const data = await res.json();
      setErrors(data.errors);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Group errors by file
  const errorsByFile = useMemo(() => {
    const grouped = new Map<string, ESLintError[]>();

    errors.forEach((error) => {
      const existing = grouped.get(error.relativeFilePath) || [];
      grouped.set(error.relativeFilePath, [...existing, error]);
    });

    // Convert to array and sort by error count (descending)
    return Array.from(grouped.entries())
      .map(
        ([filePath, fileErrors]): FileGroup => ({
          filePath,
          errors: fileErrors,
          eslintCount: fileErrors.filter((e) => e.source === "eslint").length,
          typescriptCount: fileErrors.filter((e) => e.source === "typescript")
            .length,
          totalCount: fileErrors.length,
        })
      )
      .sort((a, b) => b.totalCount - a.totalCount);
  }, [errors]);

  // Load errors on mount
  useEffect(() => {
    loadErrors();
  }, []);

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Loading errors...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 3 }}
        action={
          <Button color="inherit" size="small" onClick={loadErrors}>
            Retry
          </Button>
        }
      >
        {error}
        <Box mt={2}>
          <Typography variant="caption">
            Make sure the devtools server is running: cd devtools && npm run dev
          </Typography>
        </Box>
      </Alert>
    );
  }

  if (errors.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h5" gutterBottom>
          No errors found
        </Typography>
        <Typography variant="body1" color="text.secondary">
          All linter checks passed!
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Linter Issues
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {errors.filter((e) => e.source === "eslint").length} ESLint errors,{" "}
        {errors.filter((e) => e.source === "typescript").length} TypeScript
        errors
      </Typography>

      {/* Render file-grouped view */}
      {selectedFile ? (
        <FileErrorsView
          filePath={selectedFile}
          errors={errors.filter((e) => e.relativeFilePath === selectedFile)}
          onBack={() => setSelectedFile(null)}
          onErrorsRefresh={() => {
            setTimeout(() => loadErrors(), 1000);
          }}
        />
      ) : (
        <FileGroupView
          files={errorsByFile}
          onViewFile={setSelectedFile}
          onErrorsRefresh={() => {
            setTimeout(() => loadErrors(), 1000);
          }}
        />
      )}
    </Box>
  );
};
